import { useState, useEffect, useCallback } from "react";
import { tradeRecommendationService } from "../services/tradeRecommendationsService";
import { useApi } from "./useApi";

export const useTradeRecommendations = () => {
  const [tradeRecommendations, setTradeRecommendations] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedTradeRecommendation, setSelectedTradeRecommendation] =
    useState(null);
  const { loading, error, executeRequest, clearError } = useApi();

  // Transform trade recommendation data to ensure proper types
  const transformTradeRecommendationData = (trade) => {
    if (!trade) return null;

    return {
      ...trade,
      // Normalize field names (handle both camelCase and lowercase from backend)
      traderecommendationid:
        trade.traderecommendationid || trade.tradeRecommendationId,
      stockid: trade.stockid || trade.stockId,
      stocksymbol: trade.stocksymbol || trade.stockSymbol,
      stockname: trade.stockname || trade.stockName,
      recommendationtype: trade.recommendationtype || trade.recommendationType,
      entryprice: parseFloat(trade.entryprice || trade.entryPrice) || 0,
      targetprice: parseFloat(trade.targetprice || trade.targetPrice) || 0,
      stoploss: parseFloat(trade.stoploss || trade.stopLoss) || 0,
      timeframe: trade.timeframe,
      risklevel: trade.risklevel || trade.riskLevel,
      status: trade.status || trade.recommendationstatus,
      rationale: trade.rationale,
      technicalindicators:
        trade.technicalindicators || trade.technicalIndicators,
      fundamentalfactors: trade.fundamentalfactors || trade.fundamentalFactors,
      createdby: trade.createdby || trade.createdBy,
      createdat: trade.createdat || trade.createdAt,
      updatedat: trade.updatedat || trade.updatedAt,
    };
  };

  // Transform array of trade recommendations
  const transformTradeRecommendationsArray = (trades) => {
    if (!Array.isArray(trades)) return [];
    return trades.map(transformTradeRecommendationData);
  };

  // Fetch all trade recommendations
  // const fetchTradeRecommendations = useCallback(
  //   async (page = 1, limit = 50) => {
  //     try {
  //       const offset = (page - 1) * limit;
  //       const result = await executeRequest(
  //         tradeRecommendationService.getAllTradeRecommendations,
  //         limit,
  //         offset
  //       );


  //       const tradesData = Array.isArray(result) ? result : result.data || [];
  //       const transformedTrades =
  //         transformTradeRecommendationsArray(tradesData);
  //       setTradeRecommendations(transformedTrades);
  //       return result;
  //     } catch (err) {
  //       console.error("Error fetching trade recommendations:", err);
  //       setTradeRecommendations([]);
  //     }
  //   },
  //   [executeRequest]
  // );

  // const fetchTradeRecommendations = useCallback(
  //   async (page = 1, limit = 50, searchTerm = '') => {
  //     try {
  //       const offset = (page - 1) * limit;

  //       // Pass searchTerm to your service
  //       const result = await executeRequest(
  //         tradeRecommendationService.getAllTradeRecommendations,
  //         limit,
  //         offset,
  //         searchTerm // 👈 Add this
  //       );

  //       const tradesData = Array.isArray(result) ? result : result.data || [];
  //       const transformedTrades = transformTradeRecommendationsArray(tradesData);
  //       setTradeRecommendations(transformedTrades);
  //       return result;
  //     } catch (err) {
  //       console.error("Error fetching trade recommendations:", err);
  //       setTradeRecommendations([]);
  //     }
  //   },
  //   [executeRequest]
  // );

  const fetchTradeRecommendations = useCallback(
    async (page = 1, limit = 50, filters = {}) => {
      try {
        const offset = (page - 1) * limit;

        // If filters is a string (legacy), treat it as searchTerm
        // But ideally, filters should be an object
        const finalFilters = typeof filters === 'string'
          ? { searchTerm: filters }
          : { ...filters };

        const result = await executeRequest(
          tradeRecommendationService.getAllTradeRecommendations,
          limit,
          offset,
          finalFilters // 👈 Now passing an object
        );

        const tradesData = Array.isArray(result) ? result : result.data || [];
        const totalCount = result?.total ?? tradesData.length;
        const transformedTrades = transformTradeRecommendationsArray(tradesData);
        setTradeRecommendations(transformedTrades);
        setTotal(totalCount);
        return result;
      } catch (err) {
        console.error("Error fetching trade recommendations:", err);
        setTradeRecommendations([]);
        setTotal(0);
      }
    },
    [executeRequest]
  );

  // Fetch single trade recommendation
  const fetchTradeRecommendation = useCallback(
    async (id) => {
      try {
        const result = await executeRequest(
          tradeRecommendationService.getTradeRecommendationById,
          id
        );
        const transformedTrade = transformTradeRecommendationData(result);
        setSelectedTradeRecommendation(transformedTrade);
        return transformedTrade;
      } catch (err) {
        console.error("Error fetching trade recommendation:", err);
        setSelectedTradeRecommendation(null);
      }
    },
    [executeRequest]
  );

  // Create trade recommendation
  const createTradeRecommendation = useCallback(
    async (tradeData) => {
      try {
        // Format data with proper types
        const formattedData = {
          recoDate: tradeData.recoDate || '',
          recoTime: tradeData.recoTime || '',
          recoExitDate: tradeData.recoExitDate || '',
          exitDate: tradeData.exitDate || '',
          entryNote: tradeData.entryNote || '',
          exitTime: tradeData.exitTime || '',
          script: tradeData.script || null,
          token: tradeData.token || '',
          exitPriceLow: parseFloat(tradeData.exitPriceLow) || 0,
          exitPriceHigh: parseFloat(tradeData.exitPriceHigh) || 0,

          tradeRecommendationId: parseInt(tradeData.tradeRecommendationId, 10) || null,
          tradeTypeId: parseInt(tradeData.tradeTypeId, 10) || null,
          scriptTypeId: parseInt(tradeData.scriptTypeId, 10) || null,
          exitTypeId: parseInt(tradeData.exitTypeId, 10) || null,

          status: tradeData.status || null,
          targetOneStatus: tradeData.targetOneStatus || null,
          targetTwoStatus: tradeData.targetTwoStatus || null,
          targetThreeStatus: tradeData.targetThreeStatus || null,

          recoPriceLow: parseFloat(tradeData.recoPriceLow) || 0,
          recoPriceHigh: parseFloat(tradeData.recoPriceHigh) || 0,
          targetOne: parseFloat(tradeData.targetOne) || 0,
          targetTwo: parseFloat(tradeData.targetTwo) || 0,
          targetThree: parseFloat(tradeData.targetThree) || 0,
          stopLoss: parseFloat(tradeData.stopLoss) || 0,
          userId: localStorage.getItem("userId"),
          exitNote: tradeData.exitNote || null,
          track_only: !!tradeData.track_only,
        };

        const result = await executeRequest(
          tradeRecommendationService.createTradeRecommendation,
          formattedData
        );
        // Re-fetch to get correct data and total count from server
        await fetchTradeRecommendations();
        return result;
      } catch (err) {
        console.error("Error creating trade recommendation:", err);
        throw err;
      }
    },
    [executeRequest, fetchTradeRecommendations]
  );

  // Update trade recommendation
  const updateTradeRecommendation = useCallback(
    async (id, tradeData) => {
      try {
        // Format data with proper types
        const formattedData = {
          ...tradeData,
          stockId: tradeData.stockId || tradeData.stockid,
          recommendationType:
            tradeData.recommendationType || tradeData.recommendationtype,
          entryPrice:
            parseFloat(tradeData.entryPrice || tradeData.entryprice) || 0,
          targetPrice:
            parseFloat(tradeData.targetPrice || tradeData.targetprice) || 0,
          stopLoss: parseFloat(tradeData.stopLoss || tradeData.stoploss) || 0,
          status: tradeData.status,
          userId: localStorage.getItem("userId"),
          track_only: !!tradeData.track_only,
        };

        const result = await executeRequest(
          tradeRecommendationService.updateTradeRecommendation,
          id,
          formattedData
        );
        const transformedTrade = transformTradeRecommendationData(result);

        setTradeRecommendations((prev) =>
          prev.map((trade) =>
            trade.tradeId === id ||
              trade.tradeId === id
              ? { ...trade, ...transformedTrade }
              : trade
          )
        );

        if (
          selectedTradeRecommendation &&
          (selectedTradeRecommendation.traderecommendationid === id ||
            selectedTradeRecommendation.tradeRecommendationId === id)
        ) {
          setSelectedTradeRecommendation({
            ...selectedTradeRecommendation,
            ...transformedTrade,
          });
        }

        return transformedTrade;
      } catch (err) {
        console.error("Error updating trade recommendation:", err);
        throw err;
      }
    },
    [executeRequest, selectedTradeRecommendation, fetchTradeRecommendations]
  );

  // Delete trade recommendation
  const deleteTradeRecommendation = useCallback(
    async (id) => {
      try {
        await executeRequest(
          tradeRecommendationService.deleteTradeRecommendation,
          id
        );
        setTradeRecommendations((prev) =>
          prev.filter(
            (trade) =>
              trade.traderecommendationid !== id &&
              trade.tradeRecommendationId !== id
          )
        );
        if (
          selectedTradeRecommendation &&
          (selectedTradeRecommendation.traderecommendationid === id ||
            selectedTradeRecommendation.tradeRecommendationId === id)
        ) {
          setSelectedTradeRecommendation(null);
        }
        return true;
      } catch (err) {
        console.error("Error deleting trade recommendation:", err);
        throw err;
      }
    },
    [executeRequest, selectedTradeRecommendation]
  );

  // Search trade recommendations
  const searchTradeRecommendations = useCallback(
    async (query, filters = {}) => {
      try {
        const result = await executeRequest(
          tradeRecommendationService.searchTradeRecommendations,
          query,
          filters
        );
        const tradesData = Array.isArray(result) ? result : result.data || [];
        const transformedTrades =
          transformTradeRecommendationsArray(tradesData);
        setTradeRecommendations(transformedTrades);
        return result;
      } catch (err) {
        console.error("Error searching trade recommendations:", err);
        setTradeRecommendations([]);
      }
    },
    [executeRequest]
  );

  // Get recommendations by stock
  const getRecommendationsByStock = useCallback(
    async (stockId) => {
      try {
        const result = await executeRequest(
          tradeRecommendationService.getRecommendationsByStock,
          stockId
        );
        const tradesData = Array.isArray(result) ? result : result.data || [];
        return transformTradeRecommendationsArray(tradesData);
      } catch (err) {
        console.error("Error fetching recommendations by stock:", err);
        return [];
      }
    },
    [executeRequest]
  );

  // Get recommendations by status
  const getRecommendationsByStatus = useCallback(
    async (status) => {
      try {
        const result = await executeRequest(
          tradeRecommendationService.getRecommendationsByStatus,
          status
        );
        const tradesData = Array.isArray(result) ? result : result.data || [];
        return transformTradeRecommendationsArray(tradesData);
      } catch (err) {
        console.error("Error fetching recommendations by status:", err);
        return [];
      }
    },
    [executeRequest]
  );

  // Get recommendations by risk level
  const getRecommendationsByRiskLevel = useCallback(
    async (riskLevel) => {
      try {
        const result = await executeRequest(
          tradeRecommendationService.getRecommendationsByRiskLevel,
          riskLevel
        );
        const tradesData = Array.isArray(result) ? result : result.data || [];
        return transformTradeRecommendationsArray(tradesData);
      } catch (err) {
        console.error("Error fetching recommendations by risk level:", err);
        return [];
      }
    },
    [executeRequest]
  );

  // Clear selected trade recommendation
  const clearSelectedTradeRecommendation = useCallback(() => {
    setSelectedTradeRecommendation(null);
  }, []);

  const getScriptTypes = useCallback(async () => {
    try {
      const result = await executeRequest(tradeRecommendationService.getScriptTypes);
      return Array.isArray(result) ? result : result?.data || [];
    } catch (err) {
      console.error("Error fetching script types:", err);
      return [];
    }
  }, [executeRequest]);



  return {
    tradeRecommendations,
    total,
    selectedTradeRecommendation,
    loading,
    error,
    fetchTradeRecommendations,
    fetchTradeRecommendation,
    createTradeRecommendation,
    updateTradeRecommendation,
    deleteTradeRecommendation,
    searchTradeRecommendations,
    getRecommendationsByStock,
    getRecommendationsByStatus,
    getRecommendationsByRiskLevel,
    clearSelectedTradeRecommendation,
    clearError,
    getScriptTypes,
  };
};
